import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparkProjectDetails, SparkJob, SparkSuggestedResource, FocusSearchQuestion } from '../types/sparkTypes';
import styles from '../styles/ProjectInsightsWallPanel.module.css';
import { useAtlasStore, AtlasState } from '../../../store/atlasStore';
import { ConfirmationModal } from './modals/ConfirmationModal';
import { JobDetailModal } from './modals/JobDetailModal';
import { toast } from 'react-toastify';
import DeepResearchModal from './modals/DeepResearchModal';
import MarketLogicDisplayCard from './MarketLogicDisplayCard';
import DeepResearchDisplayCard from './DeepResearchDisplayCard';
import ActiveJobCard from './ActiveJobCard';
import PDFFileCard from './PDFFileCard';
import SuggestionCard from './SuggestionCard';
import FSQCard from './FSQCard';
import { ModeLoadingContent } from './ModeLoadingContent';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const MAX_TOTAL_FSQS = 10;

// Resources icon (matches sidebar)
const ResourcesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

interface ProjectInsightsWallPanelProps {
  project: SparkProjectDetails;
  jobs: SparkJob[];
  isClosed?: boolean;
  // Whether the tenant has AtlasProfile articles available for suggestion
  hasArticlesAvailable?: boolean;
  // Callback to notify parent of loading state changes for header button reactivity
  onLoadingStateChange?: (state: {
    isSuggestingResources: boolean;
    isAddingResource: boolean;
    isRunningResearch: boolean;
    isUploading: boolean;
    isSuggestionMode: boolean;
    selectedSuggestionCount: number;
    // FSQ mode fields
    isFSQMode: boolean;
    isFSQGenerating: boolean;
    isFSQRunning: boolean;
    selectedFSQCount: number;
    totalFSQCount: number;
    fsqWebSearchEnabled: boolean;
  }) => void;
  // Callback to navigate to the briefing step
  onNavigateToBriefing?: () => void;
  // Optional overrides for the Understanding panel label
  understandingLabel?: string;
}

// Expose action handlers and loading states to parent via ref
export interface ProjectInsightsWallPanelHandle {
  handleReferenceAction: () => void;
  handleResearchAction: () => void;
  handleFSQAction: () => void;
  handleUploadAction: () => void;
  handleCancelSuggestionMode: () => void;
  handleAddSelectedSuggestions: () => void;
  // FSQ mode handlers
  handleCancelFSQMode: () => void;
  handleRunSelectedFSQ: () => void;
  handleToggleFSQWebSearch: () => void;
  isSuggestingResources: boolean;
  isAddingResource: boolean;
  isRunningResearch: boolean;
  isUploading: boolean;
}

export const ProjectInsightsWallPanel = forwardRef<ProjectInsightsWallPanelHandle, ProjectInsightsWallPanelProps>(({ project, jobs, isClosed, hasArticlesAvailable, onLoadingStateChange, onNavigateToBriefing, understandingLabel }, ref) => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [showRemoveResourceConfirm, setShowRemoveResourceConfirm] = useState(false);
  const [resourceToRemove, setResourceToRemove] = useState<{ fqn: string | null; job_fqn: string | null; title: string; source?: string; } | null>(null);
  const [isRemovingResource, setIsRemovingResource] = useState(false);
  const [isDeepResearchModalOpen, setIsDeepResearchModalOpen] = useState(false);
  const [isSuggestionMode, setIsSuggestionMode] = useState(false);
  // FSQ inline mode state
  const [isFSQMode, setIsFSQMode] = useState(false);
  const [fsqs, setFsqs] = useState<FocusSearchQuestion[]>([]);
  const [selectedFSQIndices, setSelectedFSQIndices] = useState<Set<number>>(new Set());
  const [fsqWebSearchEnabled, setFsqWebSearchEnabled] = useState(false);
  const [suggestedResources, setSuggestedResources] = useState<SparkSuggestedResource[]>([]);
  const [selectedSuggestionFqns, setSelectedSuggestionFqns] = useState<Set<string>>(new Set());
  const [isSuggestingResources, setIsSuggestingResources] = useState(false);
  const [selectedJobFqnForDetail, setSelectedJobFqnForDetail] = useState<string | null>(null);

  const uploadResource = useAtlasStore((state) => state.uploadSparkResource);
  const removeSparkResource = useAtlasStore((state) => state.removeSparkResource);
  const runSparkDeepResearch = useAtlasStore((state) => state.runSparkDeepResearch);
  const suggestSparkResources = useAtlasStore((state) => state.suggestSparkResources);
  const addSparkResource = useAtlasStore((state) => state.addSparkResource);
  const generateFSQ = useAtlasStore((state: AtlasState) => state.generateFSQ);
  const runFSQResearch = useAtlasStore((state: AtlasState) => state.runFSQResearch);
  const setSparkUserInteractionBlockingRefresh = useAtlasStore((state: AtlasState) => state.setSparkUserInteractionBlockingRefresh);

  const isRunningResearch = useAtlasStore((state) => state.isLoadingSpark.runSparkDeepResearch);
  const isAddingResource = useAtlasStore((state) => state.isLoadingSpark.addResource);
  const isFSQGenerating = useAtlasStore((state: AtlasState) => state.isLoadingSpark.generateFSQ);
  const isFSQRunning = useAtlasStore((state: AtlasState) => state.isLoadingSpark.runFSQResearch);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const isBlocking = isSuggestionMode || isFSQMode || isDeepResearchModalOpen || isRemovingResource || isUploading || isSuggestingResources || showRemoveResourceConfirm;
    if (isBlocking) {
      setSparkUserInteractionBlockingRefresh(true);
      return () => {
        setSparkUserInteractionBlockingRefresh(false);
      };
    }
  }, [
    isSuggestionMode,
    isFSQMode,
    isDeepResearchModalOpen,
    isRemovingResource,
    isUploading,
    isSuggestingResources,
    showRemoveResourceConfirm,
    setSparkUserInteractionBlockingRefresh
  ]);

  // Filter active (pending/running) Deep Research jobs for THIS project only
  const activeDeepResearchJobs = useMemo(() => {
    return jobs.filter(job =>
      job.project_fqn === project.fqn &&
      (job._status === 'pending' || job._status === 'running') &&
      job._action_name === 'builtin/deep_research'
    );
  }, [jobs, project.fqn]);

  const allSortedResources = useMemo(() => {
    if (!project._resources) {
      return [];
    }

    // Get job FQNs of active (non-completed) deep research jobs
    const activeJobFqns = new Set(activeDeepResearchJobs.map(j => j.fqn));

    // Filter out resources linked to active jobs (they'll be shown as job cards instead)
    const filteredResources = project._resources.filter(r =>
      !(r.source === 'deep_research' && r.job_fqn && activeJobFqns.has(r.job_fqn))
    );

    const sourceOrder: { [key: string]: number } = {
      'market_logic': 1,
      'deep_research': 2,
      'user_upload': 3,
    };
    return [...filteredResources]
      .sort((a, b) => {
        const orderA = sourceOrder[a.source] ?? Infinity;
        const orderB = sourceOrder[b.source] ?? Infinity;

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        const titleA = a.title || a.fqn || '';
        const titleB = b.title || b.fqn || '';
        return titleA.localeCompare(titleB);
      });
  }, [project._resources, activeDeepResearchJobs]);

  const handleReferenceAction = useCallback(async () => {
    if (!project?.fqn) {
      toast.error("Project identifier is missing. Cannot suggest dossiers.");
      return;
    }
    // Enter suggestion mode immediately to show loading state
    setSuggestedResources([]);
    setSelectedSuggestionFqns(new Set());
    setIsSuggestionMode(true);
    setIsSuggestingResources(true);

    try {
      const suggestions = await suggestSparkResources(project.fqn);
      setSuggestedResources(suggestions.suggested_resources || []);
      if (!suggestions.suggested_resources?.length) {
        toast.info("No relevant dossiers found for this project.");
        setIsSuggestionMode(false);
      }
    } catch (error: any) {
      console.error("Failed to suggest dossiers:", error);
      const message = error.response?.data?.error || error.message || "Failed to load suggestions.";
      toast.error(`Error: ${message}`);
      setIsSuggestionMode(false);
    } finally {
      setIsSuggestingResources(false);
    }
  }, [project?.fqn, suggestSparkResources]);

  const handleAddSelectedSuggestions = useCallback(async () => {
    if (!project?.fqn || selectedSuggestionFqns.size === 0) return;

    const resourcesToAdd = Array.from(selectedSuggestionFqns).map(fqn => {
      const resource = suggestedResources.find(r => r.fqn === fqn);
      return { fqn, comment: resource?.comment || null };
    });

    const toastId = toast.loading(`Adding ${resourcesToAdd.length} dossier(s)...`);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const { fqn, comment } of resourcesToAdd) {
        try {
          await addSparkResource(project.fqn, fqn, comment);
          successCount++;
        } catch (singleError: any) {
          console.error(`Failed to add resource ${fqn}:`, singleError);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        toast.update(toastId, {
          render: `Successfully added ${successCount} dossier(s).`,
          type: 'success',
          isLoading: false,
          autoClose: 1500
        });
      } else {
        toast.update(toastId, {
          render: `Added ${successCount} dossier(s), failed to add ${errorCount}. Check console for details.`,
          type: 'warning',
          isLoading: false,
          autoClose: 5000
        });
      }

      // Exit suggestion mode
      setIsSuggestionMode(false);
      setSuggestedResources([]);
      setSelectedSuggestionFqns(new Set());

    } catch (error) {
      console.error("Unexpected error adding dossiers:", error);
      toast.update(toastId, {
        render: "An unexpected error occurred while adding dossiers.",
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    }
  }, [project?.fqn, selectedSuggestionFqns, suggestedResources, addSparkResource]);

  const handleCancelSuggestionMode = useCallback(() => {
    if (!isAddingResource) {
      setIsSuggestionMode(false);
      setSuggestedResources([]);
      setSelectedSuggestionFqns(new Set());
    }
  }, [isAddingResource]);

  const handleToggleSuggestionSelection = (fqn: string, isSelected: boolean) => {
    setSelectedSuggestionFqns(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(fqn);
      } else {
        newSet.delete(fqn);
      }
      return newSet;
    });
  };

  const handleResearchAction = useCallback(() => {
    if (!project?.fqn) {
      toast.error("Project identifier is missing. Cannot start desk research.");
      return;
    }
    setIsDeepResearchModalOpen(true);
  }, [project?.fqn]);

  const handleFSQAction = useCallback(async () => {
    if (!project?.fqn) {
      toast.error("Project identifier is missing. Cannot generate focus questions.");
      return;
    }
    if (!project.briefing) {
      toast.warning("A project briefing is required to generate focus questions.");
      return;
    }
    // Enter FSQ mode immediately to show loading state
    setFsqs([]);
    setSelectedFSQIndices(new Set());
    setFsqWebSearchEnabled(false);
    setIsFSQMode(true);

    try {
      const questions = await generateFSQ(project.fqn);
      setFsqs(questions);
      // Auto-select all questions initially
      setSelectedFSQIndices(new Set(questions.map((_, i) => i)));
      if (!questions.length) {
        toast.success("Your existing research comprehensively covers the project scope. No additional focus questions needed.");
        setIsFSQMode(false);
      }
    } catch (err: any) {
      console.error("Failed to generate FSQs:", err);
      const message = err.response?.data?.error || err.message || "Failed to generate focus questions";
      toast.error(`Error: ${message}`);
      setIsFSQMode(false);
    }
  }, [project?.fqn, project?.briefing, generateFSQ]);

  const handleCancelFSQMode = useCallback(() => {
    if (!isFSQRunning) {
      setIsFSQMode(false);
      setFsqs([]);
      setSelectedFSQIndices(new Set());
      setFsqWebSearchEnabled(false);
    }
  }, [isFSQRunning]);

  const handleRunSelectedFSQ = useCallback(async () => {
    const selectedFsqs = fsqs
      .filter((_, i) => selectedFSQIndices.has(i))
      .filter(fsq => fsq.title.trim() && fsq.question.trim());
    if (selectedFsqs.length === 0 || !project?.fqn) return;
    if (selectedFsqs.length > MAX_TOTAL_FSQS) {
      toast.error(`You can research up to ${MAX_TOTAL_FSQS} focus questions at a time.`);
      return;
    }

    try {
      await runFSQResearch(project.fqn, selectedFsqs, fsqWebSearchEnabled);
      // Exit FSQ mode
      setIsFSQMode(false);
      setFsqs([]);
      setSelectedFSQIndices(new Set());
      setFsqWebSearchEnabled(false);
    } catch (err: any) {
      console.error("Failed to start FSQ research:", err);
      const message = err.response?.data?.error || err.message || "Failed to start research";
      toast.error(`Error: ${message}`);
    }
  }, [fsqs, selectedFSQIndices, project?.fqn, fsqWebSearchEnabled, runFSQResearch]);

  const handleToggleFSQSelection = (index: number, isSelected: boolean) => {
    setSelectedFSQIndices(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });
  };

  const handleUpdateFSQ = useCallback((index: number, updated: FocusSearchQuestion) => {
    setFsqs(prev => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const [newlyAddedFSQIndex, setNewlyAddedFSQIndex] = useState<number | null>(null);

  const handleAddFSQ = useCallback(() => {
    if (fsqs.length >= MAX_TOTAL_FSQS) {
      toast.info(`You can include up to ${MAX_TOTAL_FSQS} focus questions.`);
      return;
    }

    const newFsq: FocusSearchQuestion = { title: '', question: '' };
    setFsqs(prev => {
      const next = [...prev, newFsq];
      const newIndex = next.length - 1;
      setSelectedFSQIndices(prevSel => {
        const s = new Set(prevSel);
        s.add(newIndex);
        return s;
      });
      setNewlyAddedFSQIndex(newIndex);
      return next;
    });
  }, [fsqs.length]);

  // Clear newlyAddedFSQIndex after the card has mounted
  useEffect(() => {
    if (newlyAddedFSQIndex !== null) {
      const timer = setTimeout(() => setNewlyAddedFSQIndex(null), 0);
      return () => clearTimeout(timer);
    }
  }, [newlyAddedFSQIndex]);

  const handleToggleFSQWebSearch = () => {
    setFsqWebSearchEnabled(prev => !prev);
  };

  const handleConfirmDeepResearch = async (finalBriefing: string, webSearch: boolean) => {
    if (!project?.fqn) {
      toast.error("Project identifier missing. Cannot run desk research.");
      return;
    }
    try {
      await runSparkDeepResearch(project.fqn, finalBriefing, webSearch);
      setIsDeepResearchModalOpen(false);
    } catch (error: any) {
      console.error("Failed to run desk research:", error);
      const message = error.response?.data?.error || error.message || "Failed to start desk research job.";
      toast.error(`Error: ${message}`);
    }
  };

  const handleCloseDeepResearchModal = () => {
    if (!isRunningResearch) {
      setIsDeepResearchModalOpen(false);
    }
  };

  const handleUploadAction = useCallback(() => {
    setSparkUserInteractionBlockingRefresh(true);

    const handleFocus = () => {
      console.log("Window regained focus after file picker interaction.");
      setSparkUserInteractionBlockingRefresh(false);
    };

    window.addEventListener('focus', handleFocus, { once: true });

    fileInputRef.current?.click();
  }, [setSparkUserInteractionBlockingRefresh]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!project?.fqn) {
      console.error("Cannot upload: Project FQN is missing in the component state.");
      toast.error("Cannot start upload: Project identifier is missing. Please refresh.");
      return;
    }

    event.target.value = '';

    setIsUploading(true);
    const uploadToastId = toast.loading(`Uploading ${file.name}...`);

    try {
      await uploadResource(project.fqn, file);

      toast.update(uploadToastId, {
        render: `${file.name} uploaded successfully!`,
        type: 'success',
        isLoading: false,
        autoClose: 1500
      });

    } catch (error: any) {
      console.error("Upload failed:", error);
      const message = error?.response?.data?.error || error?.message || "Upload failed. Please try again.";

      toast.update(uploadToastId, {
        render: `Upload failed: ${message}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });

    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenResourceRemoveConfirm = (resourceFqn: string | null, jobFqn: string | null, resourceTitle: string) => {
    const resource = project._resources?.find(r =>
      (resourceFqn && r.fqn === resourceFqn) ||
      (jobFqn && r.job_fqn === jobFqn)
    );

    setResourceToRemove({
      fqn: resourceFqn,
      job_fqn: jobFqn,
      title: resourceTitle,
      source: resource?.source
    });
    setShowRemoveResourceConfirm(true);
  };

  const confirmRemoveResource = async () => {
    if (!resourceToRemove || (!resourceToRemove.fqn && !resourceToRemove.job_fqn)) {
      toast.error("Resource identifier missing, cannot remove.");
      setShowRemoveResourceConfirm(false);
      setResourceToRemove(null);
      return;
    }

    setIsRemovingResource(true);
    const toastId = toast.loading("Removing resource...");
    try {
      await removeSparkResource(project.fqn, {
        resourceFqn: resourceToRemove.fqn,
        jobFqn: resourceToRemove.job_fqn
      });
      toast.update(toastId, {
        render: `Resource "${resourceToRemove.title}" removed.`,
        type: "success",
        isLoading: false,
        autoClose: 1500
      });
    } catch (error) {
      console.error("Failed to remove resource:", error);
      const message = error instanceof Error ? error.message : "An unknown error occurred.";
      toast.update(toastId, {
        render: `Failed to remove resource: ${message}`,
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
    } finally {
      setIsRemovingResource(false);
      setShowRemoveResourceConfirm(false);
      setResourceToRemove(null);
    }
  };

  const handleOpenPdfLightbox = (fileId: string, title?: string) => {
    const returnTo = `/spark/project/${encodeURIComponent(project.fqn)}`;
    navigate(`/resource/${encodeURIComponent(fileId)}`, {
      state: {
        returnTo,
        returnState: { initialStepIndex: 2 },
        title: title || 'Document'
      }
    });
  };

  const handleViewArticle = (resourceFqn: string) => {
    const resource = project._resources?.find(r => r.fqn === resourceFqn);
    const returnTo = `/spark/project/${encodeURIComponent(project.fqn)}`;
    navigate(`/viewer/${encodeURIComponent(resourceFqn)}`, {
      state: {
        returnTo,
        returnState: { initialStepIndex: 2 },
        title: resource?.title || 'Article'
      }
    });
  };

  const handleViewProfile = (profileFqn: string) => {
    const encoded = encodeURIComponent(profileFqn);
    const returnTo = `/spark/project/${encodeURIComponent(project.fqn)}`;
    navigate(`/viewer/${encoded}`, {
      state: {
        returnTo,
        returnState: { initialStepIndex: 2 },
      }
    });
  };

  useEffect(() => {
    if (!showRemoveResourceConfirm && resourceToRemove) {
      const currentResourceData = project._resources?.find(r =>
        (resourceToRemove.fqn && r.fqn === resourceToRemove.fqn) ||
        (resourceToRemove.job_fqn && r.job_fqn === resourceToRemove.job_fqn)
      );
      if (currentResourceData) {
        if (currentResourceData.fqn !== resourceToRemove.fqn || currentResourceData.job_fqn !== resourceToRemove.job_fqn) {
          setResourceToRemove(prev => prev ? ({
            ...prev,
            fqn: currentResourceData.fqn ?? null,
            job_fqn: currentResourceData.job_fqn ?? null
          }) : null);
        }
      } else {
        setResourceToRemove(null);
      }
    }
  }, [project._resources, showRemoveResourceConfirm, resourceToRemove]);

  // Expose action handlers and loading states to parent via ref
  useImperativeHandle(ref, () => ({
    handleReferenceAction,
    handleResearchAction,
    handleFSQAction,
    handleUploadAction,
    handleCancelSuggestionMode,
    handleAddSelectedSuggestions,
    // FSQ mode handlers
    handleCancelFSQMode,
    handleRunSelectedFSQ,
    handleToggleFSQWebSearch,
    isSuggestingResources,
    isAddingResource,
    isRunningResearch,
    isUploading,
  }), [
    handleReferenceAction,
    handleResearchAction,
    handleFSQAction,
    handleUploadAction,
    handleCancelSuggestionMode,
    handleAddSelectedSuggestions,
    handleCancelFSQMode,
    handleRunSelectedFSQ,
    isSuggestingResources,
    isAddingResource,
    isRunningResearch,
    isUploading,
  ]);

  // Notify parent of loading state changes for header button reactivity
  useEffect(() => {
    onLoadingStateChange?.({
      isSuggestingResources,
      isAddingResource,
      isRunningResearch,
      isUploading,
      isSuggestionMode,
      selectedSuggestionCount: selectedSuggestionFqns.size,
      // FSQ mode fields
      isFSQMode,
      isFSQGenerating,
      isFSQRunning,
      selectedFSQCount: selectedFSQIndices.size,
      totalFSQCount: fsqs.length,
      fsqWebSearchEnabled,
    });
  }, [isSuggestingResources, isAddingResource, isRunningResearch, isUploading, isSuggestionMode, selectedSuggestionFqns.size, isFSQMode, isFSQGenerating, isFSQRunning, selectedFSQIndices.size, fsqs.length, fsqWebSearchEnabled, onLoadingStateChange]);

  return (
    <div className={styles.setupPanelContainer}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".pdf"
      />

      {activeDeepResearchJobs.length > 0 && !isFSQMode && (
        <div className={styles.activeJobsSection}>
          <ul className={`${styles.resourceList} ${styles.activeJobsList}`}>
            {activeDeepResearchJobs.map(job => (
              <li key={job.fqn} className={styles.activeJobListItem}>
                <ActiveJobCard
                  job={job}
                  onClick={(j) => setSelectedJobFqnForDetail(j.fqn)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.resourceListContainer}>
        {isFSQMode ? (
          /* FSQ selection mode */
          isFSQGenerating ? (
            <ModeLoadingContent message="Generating focus questions from briefing..." />
          ) : (
            <ul className={styles.resourceList}>
              {fsqs.map((fsq, index) => (
                <FSQCard
                  key={index}
                  fsq={fsq}
                  index={index}
                  isSelected={selectedFSQIndices.has(index)}
                  onToggleSelect={handleToggleFSQSelection}
                  onUpdateFSQ={handleUpdateFSQ}
                  isDisabled={isFSQRunning}
                  isNewCard={newlyAddedFSQIndex === index}
                />
              ))}
              {!isFSQRunning && fsqs.length < MAX_TOTAL_FSQS && (
                <li
                  className={styles.addQuestionCard}
                  onClick={handleAddFSQ}
                  title="Add a custom focus question"
                >
                  <span className={styles.addQuestionLabel}>+ Add question</span>
                </li>
              )}
            </ul>
          )
        ) : isSuggestionMode ? (
          /* Suggestion selection mode - header is now in ProjectView */
          isSuggestingResources ? (
            <ModeLoadingContent message="Finding relevant dossiers..." />
          ) : (
            <ul className={styles.resourceList}>
              {suggestedResources.map((suggestion, index) => (
                <SuggestionCard
                  key={suggestion.fqn || `suggestion-${index}`}
                  suggestion={suggestion}
                  isSelected={selectedSuggestionFqns.has(suggestion.fqn)}
                  onToggleSelect={handleToggleSuggestionSelection}
                  isDisabled={isAddingResource}
                />
              ))}
            </ul>
          )
        ) : allSortedResources && allSortedResources.length > 0 ? (
          <ul className={styles.resourceList}>
            {allSortedResources.map((resource, index) => {
              if (resource.source === 'market_logic') {
                return (
                  <MarketLogicDisplayCard
                    key={resource.fqn || `market-logic-${index}`}
                    resource={resource}
                    onOpenRemoveConfirm={handleOpenResourceRemoveConfirm}
                    onOpenPdfLightbox={handleOpenPdfLightbox}
                    onViewProfile={handleViewProfile}
                  />
                );
              } else if (resource.source === 'deep_research') {
                return (
                  <DeepResearchDisplayCard
                    key={resource.fqn || resource.job_fqn || `deep-research-${index}`}
                    resource={resource}
                    onOpenRemoveConfirm={handleOpenResourceRemoveConfirm}
                    onViewArticle={handleViewArticle}
                  />
                );
              } else if (resource.source === 'user_upload') {
                return (
                  <PDFFileCard
                    key={resource.fqn || resource.job_fqn || `user-upload-${index}`}
                    resource={resource}
                    onOpenRemoveConfirm={handleOpenResourceRemoveConfirm}
                    onOpenPdfLightbox={handleOpenPdfLightbox}
                  />
                );
              } else {
                return null;
              }
            })}
          </ul>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <ResourcesIcon />
            </div>
            <h3 className={styles.emptyStateTitle}>No {(understandingLabel || 'understanding').toLowerCase()} yet</h3>
            <p className={styles.emptyStateDescription}>
              {!project.briefing
                ? 'Complete your briefing to let DeepSights investigate focus questions and develop your market understanding.'
                : 'Investigate focus questions from your briefing to develop your market and consumer understanding.'
              }
            </p>
            <div className={styles.emptyStateActions}>
              {!project.briefing && onNavigateToBriefing ? (
                <button
                  type="button"
                  className={styles.emptyStatePrimaryButton}
                  onClick={onNavigateToBriefing}
                  disabled={isClosed}
                >
                  Complete your briefing
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.emptyStatePrimaryButton} ${styles.aiButton}`}
                  onClick={handleFSQAction}
                  disabled={isClosed}
                >
                  <AutoAwesomeIcon fontSize="small" />
                  Investigate focus questions
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showRemoveResourceConfirm}
        onClose={() => setShowRemoveResourceConfirm(false)}
        onConfirm={confirmRemoveResource}
        title="Remove Resource?"
        message={resourceToRemove?.source === 'market_logic'
          ? `Are you sure you want to remove "${resourceToRemove?.title || resourceToRemove?.fqn}" from this project?\n\nYou can add it back later.`
          : `Are you sure you want to remove "${resourceToRemove?.title || resourceToRemove?.fqn}" from this project?\n\nThis cannot be undone.`
        }
        confirmText="Remove"
        isConfirming={isRemovingResource}
        variant="destructive"
      />
      <DeepResearchModal
        isOpen={isDeepResearchModalOpen}
        onClose={handleCloseDeepResearchModal}
        onConfirm={handleConfirmDeepResearch}
        initialBriefing=""
        isConfirming={isRunningResearch}
      />
      <JobDetailModal
        isOpen={!!selectedJobFqnForDetail}
        onClose={() => setSelectedJobFqnForDetail(null)}
        jobFqn={selectedJobFqnForDetail}
      />
    </div>
  );
});
